import { useEffect } from "react";

import { WrapperNodeModel } from "@/models/nodes/WrapperNodeModel";
import { scheduleOnFrame } from "@/utils/scheduleOnFrame";

import type { NodeModel } from "@/models/nodes/types";
import type { Editor } from "@tiptap/react";

export const META_EXPRESSION_NODES_CHANGED = "expressionNodesChanged";

type PositionResolver = () => number | undefined;

let pending: Map<Editor, Set<PositionResolver>> | null = null;

function flush() {
  const batches = pending;
  pending = null;

  if (!batches) {
    return;
  }

  for (const [editor, resolvers] of batches) {
    if (editor.isDestroyed) {
      continue;
    }

    const positions: number[] = [];
    for (const getPos of resolvers) {
      const pos = getPos();
      if (pos !== undefined) {
        positions.push(pos);
      }
    }

    if (positions.length === 0) {
      continue;
    }

    const { state, view } = editor;
    view.dispatch(state.tr.setMeta(META_EXPRESSION_NODES_CHANGED, positions));
  }
}

function notify(editor: Editor, getPos: PositionResolver): () => void {
  const batches = (pending ??= new Map());
  let resolvers = batches.get(editor);
  if (!resolvers) {
    batches.set(editor, (resolvers = new Set()));
  }
  resolvers.add(getPos);

  scheduleOnFrame(flush);

  const registered = resolvers;
  return () => {
    registered.delete(getPos);
  };
}

/**
 * Reports this expression whenever the number of nodes in it changes.
 *
 * Changes are batched so that multiple expressions only cause one transaction.
 */
export function useNotifyNodesChanged(
  editor: Editor,
  getPos: PositionResolver,
  expressionNode: NodeModel | undefined,
) {
  const nodesCount = expressionNode?.is(WrapperNodeModel)
    ? (expressionNode.inlineNode?.nodes.length ?? 0)
    : 0;

  useEffect(() => {
    if (!editor.isInitialized) {
      return;
    }

    return notify(editor, getPos);
  }, [nodesCount, editor, getPos]);
}
